import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CalendarModule } from 'primeng/calendar';
import { PrimeNGConfig } from 'primeng/api';
import { GenericCrudService } from '../../../../services/generic-crud.service';
import { AuthStateService } from '../../../../core/states/auth-state.service';
import { CustomDropdownComponent } from '../../../../shared/components/form-controls/custom-dropdown/custom-dropdown.component';
import { CustomMultiselectComponent } from '../../../../shared/components/form-controls/custom-multiselect/custom-multiselect.component';
import { environment } from '../../../../../environments/environment';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface DayToggle {
  id: number;
  label: string;
  shortLabel: string;
  selected: boolean;
}

interface SlotDefinition {
  start_time: string;
  end_time: string;
  practice_type: string;
  max_students: number;
}

@Component({
  selector: 'app-generador',
  standalone: true,
  imports: [CommonModule, FormsModule, CalendarModule, CustomDropdownComponent, CustomMultiselectComponent],
  templateUrl: './generador.component.html',
  styleUrls: ['./generador.component.css']
})
export class GeneradorComponent implements OnInit {

  // Días de la semana (toggle buttons)
  daysOfWeek: DayToggle[] = [
    { id: 1, label: 'Lunes', shortLabel: 'Lun', selected: true },
    { id: 2, label: 'Martes', shortLabel: 'Mar', selected: true },
    { id: 3, label: 'Miércoles', shortLabel: 'Mié', selected: true },
    { id: 4, label: 'Jueves', shortLabel: 'Jue', selected: true },
    { id: 5, label: 'Viernes', shortLabel: 'Vie', selected: true }
  ];

  // Rango de fechas (PrimeNG calendar range)
  dateRange: Date[] | null = null;

  // Grupos horarios guardados
  savedGroups: any[] = [];
  selectedGroupId: number | null = null;
  groupName: string = '';
  /** Snapshot of the loaded group to detect changes */
  private loadedGroupSnapshot: string = '';

  // Configuración para generar franjas automáticamente
  slotConfig = {
    start_time: '09:00',
    end_time: '14:00',
    duration: 60,
    practice_type: 'Abierto',
    max_students: 1
  };

  // Duraciones disponibles
  durations = [
    { label: '30 min', value: 30 },
    { label: '45 min', value: 45 },
    { label: '1 hora', value: 60 },
    { label: '1h 30min', value: 90 },
    { label: '2 horas', value: 120 }
  ];

  // Tipos de práctica disponibles
  practiceTypes = [
    { label: 'Abierto', value: 'Abierto' },
    { label: 'Maniobras', value: 'Maniobras' }
  ];

  // Franjas horarias generadas
  slots: SlotDefinition[] = [];

  // Selects
  teacherIds: number[] = [];
  vehicleId: number | null = null;
  licenseId: number | null = null;
  officeId: number | null = null;

  // Opciones de dropdowns
  teachers: any[] = [];
  vehicles: any[] = [];
  licenses: any[] = [];
  offices: any[] = [];

  // Estado
  loading: boolean = false;
  result: any = null;
  showResultModal: boolean = false;

  constructor(
    private http: HttpClient,
    private genericCrudService: GenericCrudService,
    private toastr: ToastrService,
    private router: Router,
    private primengConfig: PrimeNGConfig,
    private authStateService: AuthStateService
  ) {}

  ngOnInit(): void {
    this.primengConfig.setTranslation({
      firstDayOfWeek: 1,
      dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
      dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
      dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
      monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
      monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
      today: 'Hoy',
      clear: 'Limpiar',
      dateFormat: 'dd/mm/yy',
      weekHeader: 'Sem'
    });

    // Operadora: fijar su sucursal activa.
    const user = this.authStateService.currentUserValue;
    if (user?.roles?.includes('operator') && user?.active_office?.id) {
      this.officeId = user.active_office.id;
    }

    this.loadOptions();
    this.generateSlots();
  }

  loadOptions(): void {
    // Cada origen es independiente: si uno falla, no debe tumbar el resto.
    const safe = (url: string) => this.genericCrudService.list(url).pipe(catchError(() => of({ data: [] } as any)));

    forkJoin({
      teachers: safe('/user/teachers'),
      vehicles: safe('/config/vehicles/listAll'),
      licenses: safe('/config/license/listAll'),
      offices: safe('/config/office/listAll'),
      groups: safe('/config/schedule-template/listAll')
    }).subscribe((res) => {
      this.teachers = res.teachers.data || [];
      this.vehicles = res.vehicles.data || [];
      this.licenses = res.licenses.data || [];
      this.offices = res.offices.data || [];
      this.savedGroups = res.groups.data || [];
    });
  }

  toggleDay(day: DayToggle): void {
    day.selected = !day.selected;
    this.updateDisabledDays();
  }

  get selectedDays(): number[] {
    return this.daysOfWeek.filter(d => d.selected).map(d => d.id);
  }

  /** Days disabled in the calendar: weekends + deselected weekdays */
  calendarDisabledDays: number[] = [0, 6];

  private updateDisabledDays(): void {
    const disabled = [0, 6];
    this.daysOfWeek.forEach(d => {
      if (!d.selected) disabled.push(d.id);
    });
    this.calendarDisabledDays = disabled;
  }

  // Cargar grupo horario guardado
  onGroupChange(): void {
    if (!this.selectedGroupId) {
      this.loadedGroupSnapshot = '';
      return;
    }

    const group = this.savedGroups.find(g => g.id === this.selectedGroupId);
    if (group?.config) {
      const { slots, ...configWithoutSlots } = group.config;
      this.slotConfig = { ...configWithoutSlots };
      this.groupName = group.name;
      // Si hay slots guardados, usarlos directamente (mantiene slots eliminados manualmente)
      if (slots && Array.isArray(slots) && slots.length > 0) {
        this.slots = [...slots];
      } else {
        this.generateSlots();
      }
      this.loadedGroupSnapshot = this.getCurrentGroupSnapshot();
    }
  }

  /** Get a JSON string representing the current group state for comparison */
  private getCurrentGroupSnapshot(): string {
    return JSON.stringify({
      name: this.groupName?.trim(),
      config: { ...this.slotConfig, slots: [...this.slots] }
    });
  }

  /** Check if the loaded group has been modified */
  get isGroupModified(): boolean {
    if (!this.selectedGroupId) return false;
    return this.getCurrentGroupSnapshot() !== this.loadedGroupSnapshot;
  }

  // Generar franjas automáticamente desde la configuración
  generateSlots(): void {
    const { start_time, end_time, duration, practice_type, max_students } = this.slotConfig;

    if (!start_time || !end_time || !duration) return;

    const [startH, startM] = start_time.split(':').map(Number);
    const [endH, endM] = end_time.split(':').map(Number);

    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    if (endMinutes <= startMinutes) {
      this.slots = [];
      return;
    }

    const newSlots: SlotDefinition[] = [];
    let current = startMinutes;

    while (current + duration <= endMinutes) {
      const slotStart = this.minutesToTime(current);
      const slotEnd = this.minutesToTime(current + duration);

      newSlots.push({
        start_time: slotStart,
        end_time: slotEnd,
        practice_type: practice_type,
        max_students: max_students
      });

      current += duration;
    }

    this.slots = newSlots;
  }

  private minutesToTime(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  removeSlot(index: number): void {
    this.slots.splice(index, 1);
  }

  isFormValid(): boolean {
    // Los profesores son opcionales: se pueden crear huecos vacíos y asignar luego.
    return !!this.licenseId &&
           !!this.officeId &&
           !!this.dateRange &&
           this.dateRange.length === 2 &&
           !!this.dateRange[0] &&
           !!this.dateRange[1] &&
           this.selectedDays.length > 0 &&
           this.slots.length > 0;
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  generate(): void {
    if (!this.isFormValid()) {
      this.toastr.warning('Completa todos los campos obligatorios');
      return;
    }

    this.loading = true;
    this.result = null;

    const payload: any = {
      slots: this.slots,
      teacher_ids: this.teacherIds,
      vehicle_id: this.vehicleId || null,
      license_id: this.licenseId,
      office_id: this.officeId,
      start_date: this.formatDate(this.dateRange![0]),
      end_date: this.formatDate(this.dateRange![1]),
      days_of_week: this.selectedDays
    };

    // Si hay nombre de grupo, guardar la configuración con los slots reales
    if (this.groupName?.trim()) {
      payload.group_name = this.groupName.trim();
      payload.slot_config = { ...this.slotConfig, slots: [...this.slots] };
    }

    this.http.post<any>(`${environment.API_URL}/config/schedule-template/generate`, payload).subscribe({
      next: (res) => {
        this.loading = false;
        this.result = res.data;
        this.showResultModal = true;
        // Recargar grupos guardados por si se creó/actualizó uno
        if (this.groupName?.trim()) {
          this.reloadGroups();
        }
      },
      error: (err) => {
        this.loading = false;
        // 409 = conflictos detectados, mostrar modal con detalle
        if (err.status === 409 && err.error?.data) {
          this.result = err.error.data;
          this.showResultModal = true;
        } else {
          this.toastr.error(err.error?.message || 'Error al generar clases');
        }
      }
    });
  }

  closeResultModal(): void {
    this.showResultModal = false;
    this.result = null;
  }

  goToCuadrante(): void {
    const queryParams: any = {};
    if (this.teacherIds.length > 0) {
      queryParams.teacher_ids = this.teacherIds.join(',');
    }
    if (this.dateRange && this.dateRange[0]) {
      queryParams.date = this.formatDate(this.dateRange[0]);
    }
    this.router.navigate(['/dashboard/clasesPracticas/cuadrante'], { queryParams });
  }

  resetForm(): void {
    this.result = null;
  }

  // --- CRUD independiente de grupos horarios ---

  saveGroup(): void {
    if (!this.groupName?.trim()) {
      this.toastr.warning('Introduce un nombre para el grupo horario');
      return;
    }
    if (this.slots.length === 0) {
      this.toastr.warning('Genera al menos una franja horaria');
      return;
    }

    const payload: any = {
      name: this.groupName.trim(),
      config: { ...this.slotConfig, slots: [...this.slots] },
      active: true
    };

    if (this.selectedGroupId) {
      this.http.put<any>(`${environment.API_URL}/config/schedule-template/update/${this.selectedGroupId}`, payload).subscribe({
        next: () => {
          this.toastr.success('Grupo horario actualizado');
          this.loadedGroupSnapshot = this.getCurrentGroupSnapshot();
          this.reloadGroups();
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Error al actualizar el grupo horario');
        }
      });
    } else {
      this.http.post<any>(`${environment.API_URL}/config/schedule-template/store`, payload).subscribe({
        next: (res) => {
          this.toastr.success('Grupo horario guardado');
          const newId = res.data?.id || res.id;
          if (newId) {
            this.selectedGroupId = newId;
            this.loadedGroupSnapshot = this.getCurrentGroupSnapshot();
          }
          this.reloadGroups();
        },
        error: (err) => {
          this.toastr.error(err.error?.message || 'Error al guardar el grupo horario');
        }
      });
    }
  }

  showDeleteGroupConfirm: boolean = false;

  deleteGroup(): void {
    this.showDeleteGroupConfirm = true;
  }

  confirmDeleteGroup(): void {
    if (!this.selectedGroupId) return;

    this.http.delete<any>(`${environment.API_URL}/config/schedule-template/delete/${this.selectedGroupId}`).subscribe({
      next: () => {
        this.toastr.success('Grupo horario eliminado');
        this.selectedGroupId = null;
        this.groupName = '';
        this.loadedGroupSnapshot = '';
        this.showDeleteGroupConfirm = false;
        this.reloadGroups();
      },
      error: (err) => {
        this.toastr.error(err.error?.message || 'Error al eliminar el grupo horario');
        this.showDeleteGroupConfirm = false;
      }
    });
  }

  private reloadGroups(): void {
    this.genericCrudService.list('/config/schedule-template/listAll').subscribe({
      next: (res) => { this.savedGroups = res.data || []; }
    });
  }
}
